import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import { IdCardMock } from '../IdCardMock';
import { PreviewHeader } from '../PreviewHeader';

const INSTRUCTIONS = [
  'Place the PAN card on a flat surface.',
  'Ensure good lighting and avoid glare or shadows.',
  'Keep the entire PAN card clearly inside the frame.',
  'Hold the camera steady so all details are sharp and readable.',
];

// Designer's PanVerificationOCR screen: scanner frame (corner brackets +
// animated sweep line) around the PAN card, capture checklist, privacy notice.
// The session runtime can replace the mock card with a live camera feed via
// `children`; the builder preview renders the static IdCardMock.
export function PanVerificationOCR({ children }: { children?: ReactNode }) {
  return (
    <>
      <PreviewHeader title="PAN Verification" subtitle="Please present the front of the PAN" />

      {/* Scanner frame — designer: sc-workflow-preview__id-card-wrapper--scanner */}
      <div className="relative mb-2 w-full overflow-hidden rounded-2xl border border-[#99B6FF] bg-[#E5EDFF] px-6 py-5">
        <div className="absolute left-3 top-3 z-20 h-4 w-4 border-l-[2.5px] border-t-[2.5px] border-[#0048FF]" />
        <div className="absolute right-3 top-3 z-20 h-4 w-4 border-r-[2.5px] border-t-[2.5px] border-[#0048FF]" />
        <div className="absolute bottom-2 left-3 z-20 h-4 w-4 border-b-[2.5px] border-l-[2.5px] border-[#0048FF]" />
        <div className="absolute bottom-2 right-3 z-20 h-4 w-4 border-b-[2.5px] border-r-[2.5px] border-[#0048FF]" />
        {/* Animated scan sweep — designer: sc-workflow-preview__scanner-line */}
        <div className="animate-scan-line absolute left-0 z-30 h-0.5 w-full bg-[#0048FF] shadow-[0_0_8px_2px_rgba(0,72,255,0.5)]" />
        {children ?? <IdCardMock compact />}
      </div>

      <ul className="mb-2 flex flex-col gap-2.5">
        {INSTRUCTIONS.map((text) => (
          <li
            key={text}
            className="flex items-start gap-2 text-[length:var(--text-12)] leading-tight text-gray30"
          >
            <div className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full bg-primary text-white">
              <Check size={10} strokeWidth={4} />
            </div>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      <p className="mb-3 mt-2 text-[11px] leading-snug text-[#6B7280]">
        Your PAN details are verified securely. We do not store your personal information beyond
        what is required by law.
      </p>
    </>
  );
}
